import React from 'react';
import { am5, am5themes_Animated, am5xy } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxes } from './methods/create-axes';
import { createSeries } from './methods/create-series';
import { getHeatColorBoundaries } from '../../../helpers';
import { addLabels } from '../shared/add-labels';
import { createDownloadButton } from '../shared/create-download-button';
import { getPaddingLeft } from '../shared/helpers';

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  download: boolean;
  xAxisField: string;
  yAxisField: string;
  rotation: number;
  labelValues: boolean;
  hideHeatLegend: boolean;
  xAxisLabel: string | undefined;
  yAxisLabel: string | undefined;
  numberFormat: string | undefined;
  customMinColor: string | undefined;
  customMaxColor: string | undefined;
};

const lColor = '#FF0000';
const mColor = '#7F7F00';
const rColor = '#00FF00';

const Chart = ({
  id,
  data,
  fullScreen,
  download,
  xAxisField,
  yAxisField,
  rotation,
  xAxisLabel,
  yAxisLabel,
  numberFormat = '#.####',
  labelValues = false,
  hideHeatLegend = false,
  customMinColor,
  customMaxColor,
}: ChartProps) => {
  const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;

  const { heatMinValue, heatMaxValue } = React.useMemo(
    () => getHeatColorBoundaries(data as types.widgets.HeatMapData),
    [data]
  );

  const minColor = customMinColor || (heatMinValue == 0 ? mColor : lColor);
  const maxColor = customMaxColor || (heatMaxValue == 0 ? mColor : rColor);

  React.useEffect(() => {
    // Create root element
    const root = am5.Root.new(widgetId);

    // Remove amCharts logo
    root._logo?.dispose();

    root.numberFormatter.setAll({
      numberFormat,
    });

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        paddingLeft: getPaddingLeft(download, yAxisLabel),
        layout: root.verticalLayout,
      })
    );

    const { xAxis, yAxis } = createAxes({
      root,
      chart,
      data,
      xAxisField,
      yAxisField,
      rotation,
    });

    const heatLegend = chart.bottomAxesContainer.children.push(
      am5.HeatLegend.new(root, {
        width: am5.percent(100),
        marginTop: 10,
        orientation: 'horizontal',
        endColor: am5.color(maxColor),
        startColor: am5.color(minColor),
        startValue: heatMinValue,
        endValue: heatMaxValue,
        startText: root.numberFormatter.format(heatMinValue ?? 0),
        endText: root.numberFormatter.format(heatMaxValue ?? 0),
        visible: !hideHeatLegend,
        startOpacity: 1,
        endOpacity: 1,
      } as any)
    );

    heatLegend.startLabel.setAll({
      fontSize: 10,
    });

    heatLegend.endLabel.setAll({
      fontSize: 10,
    });

    createSeries({
      root,
      chart,
      data,
      xAxis,
      yAxis,
      xAxisField,
      yAxisField,
      heatMinValue,
      heatMaxValue,
      minColor,
      maxColor,
      labelValues,
      hideHeatLegend,
      heatLegend,
      fullScreen,
    });

    if (download) createDownloadButton(root, xAxisField, data);
    addLabels({ root, yAxis, xAxis, yAxisLabel, xAxisLabel });

    chart.appear(1000, 100);
    return () => {
      root.dispose();
    };
  }, [data]);

  return <div id={widgetId} style={{ width: '100%', height: '100%' }} />;
};

export default Chart;
